/**
 * Live client for the **roster** screener socket.
 *
 * A second screener socket, beside {@link ScreenerWebSocketClient} and
 * deliberately not a change to it. That one is keyed by a finset
 * `group`/`screen` pair — a screen name from the upstream QuantVitals engine,
 * whose `finset/` directory is empty on `qf-us`, so the route serves nothing
 * there. What is subscribed to here is a **roster**: a set of group codes, how
 * to combine them, and the computed columns wanted over the result. Two users
 * picking different groups may want the same computed column, and a multi-select
 * has no group code at all, so the old key cannot express this one. The
 * mechanics — framing, auth, backoff, replay — are lifted from the chart client
 * unchanged; only the route and the key are new.
 *
 * ## The protocol
 *
 * ```
 * client -> server   TEXT   {"type":"subscribe","codes":["RU2000"],
 *                            "mode":"union","computed":[…]}
 * server -> client   BINARY the named-frame payload the HTTP route already
 *                           returns, plus one new frame:
 *
 *                             "push" — one row per column this payload carries
 *                                      wave int32 · generation int64 · field utf8
 * ```
 *
 * The payload is the *same bytes* the `/sym/…/screen` route answers with, so it
 * needs no second decoder and no version bump: a frame a client does not know is
 * ignored, which is this wire format's whole extensibility story. The `push`
 * frame is what makes a partial update expressible — it says which columns this
 * payload speaks for, and `@qufolio/screener-grid`'s `applyScreenerPush` merges
 * exactly those and leaves every other column alone.
 *
 * ## What the client must not do on a drop
 *
 * **Blank the grid.** A disconnect carries no columns, and a push that does not
 * carry a column must not clear it — a dropped socket is the degenerate case of
 * that rule. So a close reports status and nothing else; the last values stay on
 * screen while the backoff runs, and the re-subscribe replaces them when it
 * lands. Reporting the status is what keeps that honest: the consumer renders
 * it, so a stale screen says "closed" rather than passing for a live one.
 */

import {
  decodeColumnMeta,
  decodeScreenerFrames,
  type ScreenerColumnMeta,
  type ScreenerFrame,
} from "./screener-arrow.js";
import type {
  ScreenerComputedRequest,
  ScreenerSealedConditionRequest,
} from "./screener-client.js";
import type {
  ConnectionStatus,
  TransportError,
  WebSocketFactory,
  WebSocketLike,
} from "../chart/client.js";

/** What a client is subscribed to: a universe, and what to compute over it. */
export interface ScreenerRosterSubscription {
  /** Group codes — stored groups, track lists, journal buckets. */
  codes: readonly string[];
  /** How to combine more than one code. */
  mode: "union" | "intersect";
  /**
   * Computed columns the server must materialise — exactly what
   * `serverComputedColumns` produces, which is the `needsHistory` subset. It is
   * part of the subscription key, because it is part of what the server has to
   * compute.
   */
  computed?: readonly ScreenerComputedRequest[];
  /**
   * Ticked sealed conditions — a sibling of `computed`, part of the key for the
   * same reason, and omitted when empty for the same reason.
   */
  sealedConditions?: readonly ScreenerSealedConditionRequest[];
  /**
   * A Strat screen's month, `YYYY-MM` — the stepper's (strategy lists phase 5).
   * Optional and additive, as `targetDate` is on the server: omitted means the
   * newest month, which is every screen that is not a strategy.
   */
  strategyMonth?: string;
}

/** One decoded push. */
export interface ScreenerRosterSnapshot {
  /** Named frames: `detail`, the rollups, `columns`, `timings`, and `push`. */
  frames: ScreenerFrame[];
  /** Convenience accessor for the `detail` frame. */
  detail: ScreenerFrame | undefined;
  /** Per-column metadata, empty against a server that predates the frame. */
  columns: ScreenerColumnMeta[];
}

export interface ScreenerRosterListeners {
  /** A payload arrived — on subscribe, and on every push after it. */
  onSnapshot?: (snapshot: ScreenerRosterSnapshot) => void;
  onStatus?: (status: ConnectionStatus) => void;
  onError?: (error: TransportError) => void;
}

export interface ScreenerRosterClientOptions {
  /** Full socket URL for the roster endpoint, e.g. `"ws://127.0.0.1:8010/ws/screen"`. */
  url: string;
  listeners?: ScreenerRosterListeners;
  /** Inject a WebSocket implementation (defaults to the global `WebSocket`). */
  webSocketFactory?: WebSocketFactory;
  /**
   * The Firebase ID token to open with. Called on **every** open, including a
   * reconnect — a token expires in an hour, and a socket that drops overnight
   * would otherwise reconnect with a dead one and be closed immediately,
   * forever. Injected rather than imported for D18's reason: this package is
   * vendor-agnostic and is hosted in four WebViews.
   */
  authToken?: () => string | null | Promise<string | null>;
  /** Who the stream is *for*, when that differs from who is asking. */
  actAs?: () => string | null | undefined;
  /**
   * The signed groups pass from `/auth/me`, sent in the same opening frame.
   *
   * Lets the server decide what this caller may see without asking its
   * database (`docs/plans/auth-hot-path.md`). Optional: without it the server
   * resolves the caller from Postgres, exactly as before.
   */
  pass?: () => string | null | undefined;
}

// ── Reconnect schedule ──────────────────────────────────────────────────────
//
// Copied from `QvWebSocketClient` rather than shared, for now: the two numbers
// are justified by different servers' cadences and folding them into one
// constant would make a change for the chart silently a change for the screener.

/** First retry delay; doubles per consecutive failure. */
const RECONNECT_BASE_MS = 500;

/** Delay ceiling — past this a screen is stale enough that the user has noticed. */
const RECONNECT_CAP_MS = 30_000;

/**
 * How long to wait before retry number `attempt` (0-based).
 *
 * **The jitter is not decoration.** A qv-data restart drops every open screener
 * in the same millisecond, and an undithered schedule marches all of them back
 * in lockstep onto a server that has only just finished dying. Subtractive —
 * `[d/2, d]` rather than the textbook `[0, d]` — so the cap stays a real ceiling
 * and the schedule never produces a near-instant retry, which is the one attempt
 * guaranteed to land on a server that is still starting up.
 */
function reconnectDelay(attempt: number): number {
  const ceiling = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_CAP_MS);
  return ceiling / 2 + Math.random() * (ceiling / 2);
}

function defaultWebSocketFactory(url: string): WebSocketLike {
  const Ctor = (globalThis as { WebSocket?: unknown }).WebSocket;
  if (typeof Ctor !== "function") {
    throw new Error("No global WebSocket available; pass options.webSocketFactory.");
  }
  return new (Ctor as new (u: string) => WebSocketLike)(url);
}

export class ScreenerRosterClient {
  private readonly options: ScreenerRosterClientOptions;
  private ws: WebSocketLike | null = null;
  private _status: ConnectionStatus = "idle";

  /**
   * The subscription in force — replayed on every reconnect, and the flag that
   * says a reconnect is wanted at all.
   *
   * The server keys its push channel off this and remembers nothing about a
   * client, so a reconnected socket that does not re-send it is open,
   * healthy-looking and silent forever. That exact bug cost the chart a session
   * before its own replay was added; it is not being repeated here.
   */
  private subscription: ScreenerRosterSubscription | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;

  /** True from `disconnect()` until the next `connect()`; suppresses retries. */
  private closedByCaller = false;

  constructor(options: ScreenerRosterClientOptions) {
    this.options = options;
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  connect(): Promise<void> {
    if (this.ws && (this._status === "open" || this._status === "connecting")) {
      return Promise.resolve();
    }
    // A caller reconnecting by hand supersedes whatever we had scheduled, and
    // starts the backoff over: this attempt is not the failed one's successor.
    this.closedByCaller = false;
    this.reconnectAttempt = 0;
    this.cancelReconnect();
    return this.openSocket();
  }

  /**
   * Ask for a roster — on the first selection, and on every change after it.
   *
   * **Not a reconnect.** A subscription change is one text frame on the socket
   * that is already open, which is the entire reason this is a socket: the
   * server keeps the resident frame and answers from it. Recorded before it is
   * sent, so a socket that dies mid-send resumes into the subscription the
   * caller asked for rather than the one before it, and so a call made while the
   * socket is still handshaking is honoured by `onopen` instead of dropped.
   */
  subscribe(subscription: ScreenerRosterSubscription): void {
    this.subscription = subscription;
    this.send(subscription);
  }

  /**
   * Re-send the subscription in force.
   *
   * What the Refresh button does now. The pinned protocol carries no
   * cache-dropping flag, so this means *ask again* rather than *recompute* —
   * worth knowing before reading a refreshed number as a recomputed one.
   */
  resubscribe(): void {
    if (this.subscription) this.send(this.subscription);
  }

  disconnect(): void {
    // Both of these run **before** `close()`, because a `WebSocketLike` may
    // deliver `onclose` synchronously and that handler must already know the
    // close was ours. A reconnect timer that outlives a disconnect resurrects a
    // client its owner has thrown away, and `disconnect()` runs on every unmount.
    this.closedByCaller = true;
    this.cancelReconnect();

    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.close();
    }
    this.subscription = null;
    this.setStatus("closed");
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private send(subscription: ScreenerRosterSubscription): void {
    if (!this.ws || this._status !== "open") return;
    const { codes, mode, computed, sealedConditions, strategyMonth } = subscription;
    this.ws.send(
      JSON.stringify({
        type: "subscribe",
        codes,
        mode,
        // Omitted rather than sent empty: the server derives its channel key
        // from this, and `computed: []` beside an absent one would split one
        // channel into two that compute exactly the same thing.
        ...(computed && computed.length > 0 ? { computed } : {}),
        ...(sealedConditions && sealedConditions.length > 0 ? { sealedConditions } : {}),
        // Omitted when unset, for the channel key's sake as above.
        ...(strategyMonth ? { strategyMonth } : {}),
      }),
    );
  }

  /** Open one socket and wire its handlers; shared by `connect` and the retry. */
  private openSocket(): Promise<void> {
    this.setStatus("connecting");

    const factory = this.options.webSocketFactory ?? defaultWebSocketFactory;
    const ws = factory(this.options.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    return new Promise<void>((resolve, reject) => {
      ws.onopen = () => {
        // **The auth frame goes first, before the subscribe.** A browser cannot
        // set `Authorization` on a WebSocket handshake, so qv-data reads the
        // token from the opening message and closes the socket (4401) if it does
        // not arrive. Text, which is what distinguishes both control frames from
        // the binary payloads coming the other way.
        void (async () => {
          try {
            const token = await this.options.authToken?.();
            if (token) {
              const actAs = this.options.actAs?.() || undefined;
              const pass = this.options.pass?.() || undefined;
              ws.send(
                JSON.stringify({
                  type: "auth",
                  token,
                  ...(actAs ? { actAs } : {}),
                  ...(pass ? { pass } : {}),
                }),
              );
            }
          } catch {
            // A token we could not mint is the same as none: the server closes
            // the socket and the reconnect path handles it. Throwing here would
            // take out `onopen` and leave the socket half-alive.
          }
          this.reconnectAttempt = 0;
          this.setStatus("open");
          resolve();
          // A socket that opens with a subscription already recorded is a
          // replacement for one that dropped, and nobody else will re-send it —
          // the consumer wired a snapshot callback and never hears about the
          // close at all.
          if (this.subscription) this.send(this.subscription);
        })();
      };
      ws.onmessage = (ev) => {
        this.handleMessage(ev.data);
      };
      ws.onclose = () => {
        // Only the socket we are still holding may declare the feed dead. A
        // superseded one closing — a `disconnect()` mid-handshake, or a browser
        // delivering the close of a socket already replaced — would otherwise
        // null out the live socket and take the screen down with it.
        if (this.ws !== ws) return;
        this.ws = null;
        this.setStatus("closed");
        this.scheduleReconnect();
      };
      ws.onerror = (cause) => {
        this.emitError({ source: "socket", message: "WebSocket error", cause });
        if (this._status === "connecting") reject(new Error("WebSocket error"));
      };
    });
  }

  /**
   * Wait out the backoff, then try again.
   *
   * Guarded on a timer already being pending because `onclose` and a throwing
   * factory can both reach here for one failure, and two live timers would halve
   * the delay every time they raced.
   */
  private scheduleReconnect(): void {
    if (this.closedByCaller || !this.subscription) return;
    if (this.reconnectTimer !== null) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closedByCaller) return;
      try {
        void this.openSocket().catch(() => {
          // A refused handshake announces itself through `onerror` and then
          // `onclose`, and the close schedules the next attempt. This catch
          // exists only so the rejection is not an unhandled one.
        });
      } catch (cause) {
        // The factory itself threw, so there is no socket to deliver an
        // `onclose` and the retry chain would end here, silently.
        this.emitError({ source: "socket", message: "reconnect failed", cause });
        this.scheduleReconnect();
      }
    }, reconnectDelay(this.reconnectAttempt++));
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private handleMessage(data: unknown): void {
    let bytes: Uint8Array;
    if (data instanceof ArrayBuffer) bytes = new Uint8Array(data);
    else if (data instanceof Uint8Array) bytes = data;
    else {
      // Text this way is always a protocol error: control frames go client to
      // server, payloads come back binary.
      this.emitError({
        source: "decode",
        message: `expected a binary screener payload, got ${typeof data}`,
      });
      return;
    }

    let frames: ScreenerFrame[];
    try {
      frames = decodeScreenerFrames(bytes);
    } catch (cause) {
      // A malformed push must not kill the subscription — the next one may well
      // be fine, and dropping the socket would lose the good frames too.
      this.emitError({
        source: "decode",
        message: "failed to decode screener payload",
        cause,
      });
      return;
    }

    this.options.listeners?.onSnapshot?.({
      frames,
      detail: frames.find((f) => f.name === "detail"),
      columns: decodeColumnMeta(frames),
    });
  }

  private emitError(error: TransportError): void {
    this.options.listeners?.onError?.(error);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this._status === status) return;
    this._status = status;
    this.options.listeners?.onStatus?.(status);
  }
}
